import { ApolloError } from '@apollo/client';

export interface ErrorInfo {
  message: string;
  type: 'network' | 'graphql' | 'auth' | 'unknown';
  originalError?: unknown;
}

export const parseApolloError = (error: ApolloError): ErrorInfo => {
  // Handle network errors
  if (error.networkError) {
    const networkError = error.networkError;
    
    // Handle authentication errors
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      return {
        message: 'Authentication failed. Please log in again.',
        type: 'auth',
        originalError: networkError
      };
    }
    
    // Handle other network errors
    if ('statusCode' in networkError) {
      const statusCode = networkError.statusCode;
      if (statusCode >= 500) {
        return {
          message: 'Server error. Please try again later.',
          type: 'network',
          originalError: networkError
        };
      } else if (statusCode >= 400) {
        return {
          message: 'Request failed. Please check your input and try again.',
          type: 'network',
          originalError: networkError
        };
      }
    }
    
    return {
      message: 'Network error. Please check your connection and try again.',
      type: 'network',
      originalError: networkError
    };
  }
  
  // Handle GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const graphQLError = error.graphQLErrors[0];
    
    // Check for specific GraphQL error types
    if (graphQLError.extensions?.code === 'UNAUTHENTICATED') {
      return {
        message: 'Authentication required. Please log in.',
        type: 'auth',
        originalError: graphQLError
      };
    }
    
    if (graphQLError.extensions?.code === 'FORBIDDEN') {
      return {
        message: 'You do not have permission to perform this action.',
        type: 'auth',
        originalError: graphQLError
      };
    }
    
    // Use the GraphQL error message if available
    return {
      message: graphQLError.message || 'An error occurred while processing your request.',
      type: 'graphql',
      originalError: graphQLError
    };
  }
  
  // Fallback for unknown errors
  return {
    message: error.message || 'An unexpected error occurred. Please try again.',
    type: 'unknown',
    originalError: error
  };
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApolloError) {
    return parseApolloError(error).message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred. Please try again.';
};
